import { FormHelperText, FormLabel } from '@mui/material';
import clsx from 'clsx';
import type { KeyboardEvent, MouseEvent, SelectHTMLAttributes } from 'react';
import PhoneInput, { Props as PhoneInputProps } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { PhoneInputContainer, type StyledPhoneInputSurface } from './styles';

type Props = PhoneInputProps<{
  isHighlighted?: boolean;
  error?: boolean;
  label?: string;
  errorMessage?: string;
  /** Passed through to the underlying phone input (not all typings expose it). */
  placeholder?: string;
  /** `default`: bordered interactive look. `grey120`: flat read-only / template preview. */
  surface?: StyledPhoneInputSurface;
}>;

export default function StyledPhoneInput({
  isHighlighted,
  className,
  countrySelectProps,
  error,
  label,
  errorMessage,
  placeholder = '+41 1234 5678',
  readOnly,
  surface = 'default',
  ...props
}: Props) {
  const countrySelect = countrySelectProps as SelectHTMLAttributes<HTMLSelectElement> | undefined;

  return (
    <PhoneInputContainer
      error={error}
      label={!!label}
      highlighted={isHighlighted}
      readOnly={readOnly}
      surface={surface}
    >
      {label && <FormLabel>{label}</FormLabel>}
      <PhoneInput
        international
        limitMaxLength
        defaultCountry='CH'
        {...props}
        readOnly={readOnly}
        placeholder={placeholder}
        className={clsx('phone-input-root', className)}
        countrySelectProps={{
          ...countrySelect,
          className: clsx('country-select', countrySelect?.className),
          'aria-readonly': readOnly || undefined,
          onMouseDown: (event: MouseEvent<HTMLSelectElement>) => {
            if (readOnly) {
              event.preventDefault();
              return;
            }
            countrySelect?.onMouseDown?.(event);
          },
          onKeyDown: (event: KeyboardEvent<HTMLSelectElement>) => {
            if (readOnly) {
              event.preventDefault();
              return;
            }
            countrySelect?.onKeyDown?.(event);
          },
          arrowComponent: () => null, // Hide default arrow to avoid confusion with the custom one
        }}
      />
      {error && errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
    </PhoneInputContainer>
  );
}
